import { useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, MenuItem, Stack, TextField, Typography,
  createFilterOptions,
} from '@mui/material';
import { AddRounded } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { destinationsApi, type NamedRef, type PlaceLevel } from '@/api/places.api';
import { ApiError } from '@/lib/apiClient';
import { useNotify } from '@/components/common/useNotify';
import { humanizeError } from '@/utils/errorMessage';
import SearchSelect, { SHORT_LIST, typeAhead } from '@/components/common/SearchSelect';

const ADD_NEW: NamedRef = { id: -1, code: '', name: '+ Add new' };
const filter = createFilterOptions<NamedRef>({ stringify: (o) => `${o.name} ${o.code}` });

/**
 * Destination dropdown ending with "+ Add new" (FR-UI-05): the pop-up creates a draft destination on the spot
 * (name, level, country, state) and selects it. An exact duplicate is blocked and the existing one offered; a
 * near-spelling needs confirmation (FR-UI-06). The rest of the destination is completed later in Destinations.
 */
export default function DestinationSelect({ value, onChange, exclude, error, helperText, disabled, placeholder }: {
  value: number | null | undefined; onChange: (id: number | null) => void; exclude?: number[]; error?: boolean;
  helperText?: string; disabled?: boolean; placeholder?: string;
}) {
  const qc = useQueryClient();
  const dests = useQuery({ queryKey: ['destination-options', 'all'], queryFn: () => destinationsApi.options(), staleTime: 300_000 });
  const [adding, setAdding] = useState<string | null>(null);
  const [typed, setTyped] = useState('');
  const options = (dests.data ?? []).filter((d) => !exclude?.includes(d.id));
  const current = (dests.data ?? []).find((d) => d.id === value) ?? null;

  return (
    <>
      <Autocomplete size="small" options={options} value={current} disabled={disabled} loading={dests.isLoading}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        getOptionLabel={(o) => (o.id === ADD_NEW.id ? o.name : `${o.name} (${o.code})`)}
        // A long list shows only "+ Add new" until 2–3 letters are typed, then the matches best first.
        filterOptions={(opts, state) => [...(opts.length > SHORT_LIST && !state.inputValue.trim() ? [] : typeAhead((o: NamedRef) => o.name, (o) => o.code)(opts, state)), ADD_NEW]}
        renderOption={(props, o) => {
          const { key, ...rest } = props as typeof props & { key: string };
          return o.id === ADD_NEW.id
            ? <li key="add-new" {...rest} style={{ borderTop: '1px solid #E5E7EB', color: '#1D4E89', fontWeight: 600 }}><AddRounded fontSize="small" sx={{ mr: 0.5 }} />Add new</li>
            : <li key={key} {...rest}>{o.name}<Typography component="span" sx={{ ml: 1, color: 'text.secondary', fontSize: 13 }}>{o.code}</Typography></li>;
        }}
        onInputChange={(_, v, reason) => { if (reason === 'input') setTyped(v); }}
        onChange={(_, v) => {
          if (v?.id === ADD_NEW.id) { setAdding(typed); return; }
          onChange(v?.id ?? null);
        }}
        renderInput={(pp) => <TextField {...pp} error={error} helperText={helperText} placeholder={placeholder} />} />
      {adding !== null && (
        <QuickAddDestinationDialog initialName={adding} onClose={() => setAdding(null)}
          onPicked={(id) => { setAdding(null); qc.invalidateQueries({ queryKey: ['destination-options'] }); onChange(id); }} />
      )}
    </>
  );
}

export function QuickAddDestinationDialog({ initialName, onClose, onPicked }: { initialName: string; onClose: () => void; onPicked: (id: number) => void }) {
  const notify = useNotify();
  const [name, setName] = useState(initialName.toUpperCase());
  const [level, setLevel] = useState<PlaceLevel>('CityTown');
  const [country, setCountry] = useState('IN');
  const [state, setState] = useState('');
  const [problem, setProblem] = useState<{ text: string; existing?: NamedRef; near?: boolean } | null>(null);
  const geo = useQuery({ queryKey: ['destination-geo-operating', country], queryFn: () => destinationsApi.geo(country, true), staleTime: 600_000 });
  // Starts in India only when the company operates in India.
  useEffect(() => {
    if (geo.data && geo.data.countries.length > 0 && !geo.data.countries.some((c) => c.iso2 === country)) { setCountry(geo.data.countries[0].iso2); setState(''); }
  }, [geo.data, country]);

  const create = useMutation({
    mutationFn: (confirmNear: boolean) => destinationsApi.create({
      name: name.trim(), alsoKnownAs: [], level, parentId: null, countryIso2: country, stateCode: state || null, latitude: null, longitude: null,
      elevationFt: null, timeZone: null, types: [], bestMonths: [], weatherNotes: null, permitRequired: false, permitDetails: null,
      transferPoints: [], shortDescription: null, longDescription: null, coverFileId: null, galleryFileIds: [], tags: [], activate: false,
      confirmNearDuplicate: confirmNear,
    }),
  });

  const save = async (confirmNear = false) => {
    setProblem(null);
    try {
      const d = await create.mutateAsync(confirmNear);
      notify.success(`${d.name} (${d.code}) added as a draft. Complete it later in Destinations.`);
      onPicked(d.id);
    } catch (e) {
      if (e instanceof ApiError && e.errorCode === 'DUPLICATE') {
        const match = (await destinationsApi.options(name.trim()).catch(() => [] as NamedRef[])).find((o) => o.name === name.trim().toUpperCase());
        setProblem({ text: e.message, existing: match });
      } else if (e instanceof ApiError && e.errorCode === 'NEAR_DUPLICATE') setProblem({ text: e.message, near: true });
      else setProblem({ text: humanizeError(e, 'Could not add the destination.') });
    }
  };

  const missing = name.trim().length < 2 ? 'Destination name is mandatory.' : country === 'IN' && !state ? 'State is mandatory for India.' : null;

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogContent sx={{ pt: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box sx={{ width: 34, height: 34, borderRadius: 1.5, bgcolor: '#FCEFEF', color: 'primary.main', display: 'grid', placeItems: 'center', flexShrink: 0 }}><AddRounded /></Box>
          <Box sx={{ flex: 1 }}>
            <Typography fontWeight={700} fontSize={18} sx={{ mb: 1.5 }}>Add new destination</Typography>
            <Stack spacing={1.5}>
              <TextField size="small" label="Destination name *" value={name} onChange={(e) => { setName(e.target.value.toUpperCase()); setProblem(null); }}
                error={!!problem && !problem.near} autoFocus />
              {problem && (
                <Alert severity={problem.near ? 'warning' : 'error'} sx={{ py: 0 }}>
                  {problem.text}
                  {problem.existing && <Box sx={{ mt: 1 }}><Button size="small" variant="outlined" onClick={() => onPicked(problem.existing!.id)}>Select '{problem.existing.name}'</Button></Box>}
                </Alert>
              )}
              <TextField select size="small" label="Level *" value={level} onChange={(e) => setLevel(e.target.value as PlaceLevel)}>
                <MenuItem value="CityTown">City/Town</MenuItem><MenuItem value="Village">Village</MenuItem></TextField>
              <SearchSelect label="Country *" clearable={false} value={country || null} onChange={(v) => { setCountry(v ?? ''); setState(''); }}
                options={(geo.data?.countries ?? [{ iso2: 'IN', name: 'India' }]).map((c) => ({ value: c.iso2, label: c.name, hint: c.iso2 }))} />
              {(geo.data?.states.length ?? 0) > 0 && (
                <SearchSelect label={country === 'IN' ? 'State *' : 'State / province'} value={state || null} onChange={(v) => setState(v ?? '')}
                  options={geo.data!.states.map((s) => ({ value: s.code, label: s.name }))} />
              )}
              <Typography variant="caption" color="text.secondary">Saved as a draft with its ID; add the map pin, description and cover later to activate it.</Typography>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant="outlined" color="inherit" onClick={onClose}>Cancel</Button>
        {problem?.near
          ? <Button variant="contained" disabled={create.isPending} onClick={() => save(true)}>It is a different place, add</Button>
          : <Button variant="contained" disabled={!!missing || create.isPending || (!!problem && !problem.near)} onClick={() => save()}>{create.isPending ? 'Adding…' : 'Add and select'}</Button>}
      </DialogActions>
    </Dialog>
  );
}
